import React, { useEffect, useMemo, useState } from 'react';
import {
  HiMiniChatBubbleLeftRight,
  HiMiniDocumentText,
  HiMiniInformationCircle,
  HiMiniQuestionMarkCircle,
  HiMiniWrenchScrewdriver,
  HiMiniXCircle,
} from 'react-icons/hi2';
import { useAuth } from '../hooks/useAuth';
import '../styles/admin-common.css';
import { formatDate, formatDateTime } from '../utils/format';

interface ReportItem {
  report_id: string;
  session_id?: string;
  question_number?: number;
  report_type: string;
  status: 'pending' | 'resolved' | 'rejected';
  author_name?: string;
  created_at: string;
  description?: string;
  question_info?: {
    question_id?: string;
    question_text?: string;
    exam_type?: string;
    options?: Array<{ option_number: number; option_text: string }>;
  };
  admin_response?: string;
}

interface ReportManagementPageProps {
  onEditQuestion?: (examType: string, questionId: string) => void;
}

const REPORTS_PER_PAGE = 20;

const ReportManagementPage: React.FC<ReportManagementPageProps> = ({ onEditQuestion }) => {
  const { user } = useAuth();
  const [reports, setReports] = useState<ReportItem[]>([]);
  const [selectedReport, setSelectedReport] = useState<ReportItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalReports, setTotalReports] = useState(0);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalReports / REPORTS_PER_PAGE)), [totalReports]);

  const apiCall = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('authToken');
    return fetch(`${import.meta.env.VITE_API_BASE_URL}${url}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    });
  };

  const loadReports = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: String(REPORTS_PER_PAGE),
        offset: String((currentPage - 1) * REPORTS_PER_PAGE),
      });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (typeFilter !== 'all') params.set('type', typeFilter);

      const response = await apiCall(`/admin/reports?${params.toString()}`);
      if (response.ok) {
        const result = await response.json();
        setReports(result.data.reports || []);
        setTotalReports(result.data.pagination?.total || result.data.reports?.length || 0);
      }
    } catch (error) {
      console.error('Failed to load reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateReportStatus = async (reportId: string, status: ReportItem['status']) => {
    try {
      const response = await apiCall(`/admin/reports/${reportId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status }),
      });
      if (response.ok) {
        await loadReports();
        if (selectedReport?.report_id === reportId) {
          setSelectedReport(null);
        }
      }
    } catch (error) {
      console.error('Failed to update report status:', error);
    }
  };

  const getStatusLabel = (status: string) => {
    if (status === 'resolved') return '해결됨';
    if (status === 'rejected') return '반려됨';
    return '대기중';
  };

  const getStatusBadgeClass = (status: string) => {
    if (status === 'resolved') return 'success';
    if (status === 'rejected') return 'danger';
    return 'warning';
  };

  const getReportTypeMeta = (reportType: string) => {
    switch (reportType) {
      case 'wrong_answer':
        return { label: '오답 신고', icon: <HiMiniXCircle /> };
      case 'unclear_question':
        return { label: '문제 불명확', icon: <HiMiniQuestionMarkCircle /> };
      case 'technical_issue':
        return { label: '기술 문제', icon: <HiMiniWrenchScrewdriver /> };
      case 'typo':
        return { label: '오탈자', icon: <HiMiniDocumentText /> };
      case 'missing_info':
        return { label: '정보 부족', icon: <HiMiniInformationCircle /> };
      case 'other':
      default:
        return { label: '기타', icon: <HiMiniChatBubbleLeftRight /> };
    }
  };

  const getReporterName = (report: ReportItem) => {
    const raw = String(report.author_name || '').trim();
    if (!raw) return '알 수 없음';
    return raw.includes('@') ? raw.split('@')[0] : raw;
  };

  const sanitizeReportDescription = (description?: string) => {
    if (!description) return '';
    return description
      .replace(/세션\s*ID\s*:\s*[^\n\r]*[\n\r]?/gi, '')
      .replace(/문제\s*번호\s*:\s*[^\n\r]*[\n\r]?/gi, '')
      .replace(/신고\s*유형\s*:\s*[^\n\r]*[\n\r]?/gi, '')
      .replace(/session\s*id\s*:\s*[^\n\r]*[\n\r]?/gi, '')
      .replace(/^내용\s*:\s*/gim, '')
      .replace(/\n{2,}/g, '\n')
      .trim();
  };

  const renderTypeChip = (reportType: string) => {
    const typeMeta = getReportTypeMeta(reportType);
    return (
      <span className="report-type-chip">
        <span className="report-type-chip__icon">{typeMeta.icon}</span>
        <span>{typeMeta.label}</span>
      </span>
    );
  };

  const canEditReportedQuestion = (report: ReportItem) => Boolean(
    onEditQuestion && report.question_info?.exam_type && report.question_info.question_id,
  );

  const editReportedQuestion = (report: ReportItem) => {
    const examType = report.question_info?.exam_type;
    const questionId = report.question_info?.question_id;
    if (examType && questionId) {
      setSelectedReport(null);
      onEditQuestion?.(examType, questionId);
    }
  };

  useEffect(() => {
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, statusFilter, typeFilter]);

  if (!user?.is_admin) {
    return (
      <div className="admin-page">
        <div className="access-denied">
          <h2>접근 권한이 없습니다</h2>
          <p>관리자 권한이 필요한 페이지입니다.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page report-management-page">
      <header className="admin-page-title">
        <span className="admin-page-title__eyebrow">관리자 워크스페이스</span>
        <h1>신고 관리</h1>
        <p>문제 신고를 검토하고 해결 또는 반려 상태로 처리합니다.</p>
      </header>

      <section className="admin-section report-management-panel">
        <div className="admin-header report-management-panel__header">
          <div>
            <h2>신고 목록</h2>
            <p>신고 유형, 상태, 문제 위치와 상세 내용을 확인합니다.</p>
          </div>
          <div className="report-management-controls">
            <div className="admin-form-inline report-management-controls__group">
              <select
                className="admin-form-control sm select"
                value={statusFilter}
                onChange={(event) => {
                  setStatusFilter(event.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">모든 상태</option>
                <option value="pending">대기중</option>
                <option value="resolved">해결됨</option>
                <option value="rejected">반려됨</option>
              </select>
              <select
                className="admin-form-control sm select"
                value={typeFilter}
                onChange={(event) => {
                  setTypeFilter(event.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">모든 유형</option>
                <option value="wrong_answer">오답 신고</option>
                <option value="unclear_question">문제 불명확</option>
                <option value="technical_issue">기술 문제</option>
                <option value="typo">오탈자</option>
                <option value="missing_info">정보 부족</option>
                <option value="other">기타</option>
              </select>
            </div>
          </div>
        </div>

        <div className="reports-stats">
          <span>총 {totalReports}개 신고</span>
          <span>페이지 {currentPage} / {totalPages}</span>
        </div>

        <div className="report-admin-table-wrap">
          <table className="admin-table report-admin-table">
            <thead>
              <tr>
                <th className="col-report-id">ID</th>
                <th className="col-report-type">유형</th>
                <th className="col-report-author">신고자</th>
                <th className="col-report-question">문제</th>
                <th className="col-report-content">신고 내용</th>
                <th className="col-report-status">상태</th>
                <th className="col-report-date">신고일</th>
                <th className="col-report-action">작업</th>
              </tr>
            </thead>
            <tbody>
              {reports.length === 0 && (
                <tr>
                  <td colSpan={8} className="admin-empty-cell">
                    {loading ? '신고를 불러오는 중입니다.' : '표시할 신고가 없습니다.'}
                  </td>
                </tr>
              )}
              {reports.map((report) => (
                <tr key={report.report_id}>
                  <td className="col-report-id">
                    <span className="admin-badge info report-id-badge">#{report.report_id.slice(-6)}</span>
                  </td>
                  <td className="col-report-type">{renderTypeChip(report.report_type)}</td>
                  <td className="col-report-author">
                    <span className="admin-text-sm">{getReporterName(report)}</span>
                  </td>
                  <td className="col-report-question">
                    <div className="report-question-cell">
                      <span>세션 {report.session_id?.slice(-6) || '-'}</span>
                      <span>문제 {report.question_number ? `${report.question_number}번` : '-'}</span>
                    </div>
                  </td>
                  <td className="col-report-content" title={sanitizeReportDescription(report.description)}>
                    <div className="report-content-cell">
                      {sanitizeReportDescription(report.description) || '-'}
                    </div>
                  </td>
                  <td className="col-report-status">
                    <span className={`admin-badge ${getStatusBadgeClass(report.status)}`}>
                      {getStatusLabel(report.status)}
                    </span>
                  </td>
                  <td className="col-report-date">
                    <span className="admin-text-sm">{formatDate(report.created_at)}</span>
                  </td>
                  <td className="col-report-action">
                    <div className="admin-flex">
                      {canEditReportedQuestion(report) && (
                        <button
                          className="admin-btn sm outline"
                          type="button"
                          onClick={() => editReportedQuestion(report)}
                        >
                          문제 수정
                        </button>
                      )}
                      <button className="admin-btn sm primary" type="button" onClick={() => setSelectedReport(report)}>
                        상세
                      </button>
                      {report.status === 'pending' && (
                        <>
                          <button
                            className="admin-btn sm success"
                            type="button"
                            onClick={() => updateReportStatus(report.report_id, 'resolved')}
                          >
                            해결
                          </button>
                          <button
                            className="admin-btn sm danger"
                            type="button"
                            onClick={() => updateReportStatus(report.report_id, 'rejected')}
                          >
                            반려
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalReports > REPORTS_PER_PAGE && (
          <div className="admin-pagination">
            <button
              className="admin-btn secondary"
              type="button"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
            >
              이전
            </button>
            <span className="page-info">
              {currentPage} / {totalPages} 페이지
            </span>
            <button
              className="admin-btn secondary"
              type="button"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              다음
            </button>
          </div>
        )}
      </section>

      {selectedReport && (
        <div className="report-detail-modal-overlay">
          <div className="report-detail-modal">
            <div className="report-detail-modal__header">
              <h3>신고 상세 정보</h3>
              <button className="report-detail-modal__close" type="button" onClick={() => setSelectedReport(null)}>
                닫기
              </button>
            </div>
            <div className="report-detail-modal__body">
              <div className="report-detail-grid">
                <div className="report-detail-field">
                  <span>ID</span>
                  <strong>{selectedReport.report_id}</strong>
                </div>
                <div className="report-detail-field">
                  <span>유형</span>
                  <strong>{renderTypeChip(selectedReport.report_type)}</strong>
                </div>
                <div className="report-detail-field">
                  <span>신고자</span>
                  <strong>{getReporterName(selectedReport)}</strong>
                </div>
                <div className="report-detail-field">
                  <span>신고일</span>
                  <strong>{formatDateTime(selectedReport.created_at)}</strong>
                </div>
                <div className="report-detail-field">
                  <span>상태</span>
                  <strong>
                    <span className={`admin-badge ${getStatusBadgeClass(selectedReport.status)}`}>
                      {getStatusLabel(selectedReport.status)}
                    </span>
                  </strong>
                </div>
                <div className="report-detail-field">
                  <span>문제 위치</span>
                  <strong>
                    세션 {selectedReport.session_id || '-'} / 문제 {selectedReport.question_number ? `${selectedReport.question_number}번` : '-'}
                  </strong>
                </div>
              </div>

              <div className="report-detail-section">
                <h4>문제 내용</h4>
                <div className="report-detail-box">
                  {selectedReport.question_info?.question_text || '-'}
                </div>
              </div>

              {selectedReport.question_info?.options?.length ? (
                <div className="report-detail-section">
                  <h4>선택지</h4>
                  <div className="options-list">
                    {selectedReport.question_info.options.map((option) => (
                      <div key={option.option_number} className="option-item">
                        <span className="option-number">{option.option_number}.</span>
                        <span className="option-text">{option.option_text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="report-detail-section">
                <h4>신고 내용</h4>
                <div className="report-detail-box">
                  {sanitizeReportDescription(selectedReport.description) || '-'}
                </div>
              </div>
            </div>
            <div className="report-detail-modal__footer">
              {canEditReportedQuestion(selectedReport) && (
                <button
                  className="admin-btn primary"
                  type="button"
                  onClick={() => editReportedQuestion(selectedReport)}
                >
                  문제 수정
                </button>
              )}
              {selectedReport.status === 'pending' && (
                <>
                  <button
                    className="admin-btn success"
                    type="button"
                    onClick={() => updateReportStatus(selectedReport.report_id, 'resolved')}
                  >
                    해결 처리
                  </button>
                  <button
                    className="admin-btn danger"
                    type="button"
                    onClick={() => updateReportStatus(selectedReport.report_id, 'rejected')}
                  >
                    반려 처리
                  </button>
                </>
              )}
              <button className="admin-btn secondary" type="button" onClick={() => setSelectedReport(null)}>
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportManagementPage;
